import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, MapPin, Maximize2, Navigation, ChevronRight } from 'lucide-react';
import { useSavedItems } from '../contexts/SavedItemsContext';
import { useAuth } from '../contexts/AuthContext';
import RegisterPromptModal from './RegisterPromptModal';
import './CompoundCard.css';

/**
 * In-chat compound card (Figma-styled). Binds to the Indexing Tool / Broker
 * search-result shape: project_name, location, average_area, payment_plans,
 * images, compound_id, distance_km, plus AI insights (why_recommended, consider,
 * lifestyle_match). Elements without backing data are hidden — no fabricated
 * prices or ratings.
 */
const slug = (name) => (name || '')
  .toLowerCase()
  .replace(/\s+/g, '-')
  .replace(/[^a-z0-9-]/g, '');

function asPoints(text) {
  if (!text || typeof text !== 'string') return [];
  const trimmed = text.trim();
  if (!trimmed) return [];
  return trimmed.split(/\n|•|\.\s+/).map((s) => s.replace(/^[-*]\s*/, '').trim()).filter(Boolean);
}

export default function CompoundCard({ compound }) {
  const { isSaved, toggleSave } = useSavedItems();
  const { isAuthenticated } = useAuth();
  const [registerPrompt, setRegisterPrompt] = useState(null);

  const name = compound.project_name || compound.name || compound.title || 'Compound';
  const id = compound.compound_id || compound.id || slug(name);
  const location = compound.location || compound.area || '';
  const area = compound.average_area || compound.area_range || '';
  const facilities = compound.facilities || compound.amenities || '';
  const hasPayment = !!(compound.payment_plans || compound.payment_summary);
  const distanceKm = typeof compound.distance_km === 'number' ? compound.distance_km : null;
  const img = (compound.images && compound.images[0]) || compound.image;
  const imgUrl = img && (typeof img === 'string' ? img : img.url);

  // The compound answers "where"; these answer "what can I actually buy".
  // Already filtered server-side to this buyer's budget and bedroom count.
  const units = Array.isArray(compound.matching_units) ? compound.matching_units : [];
  const unitsTotal = compound.total_matching_units || units.length;

  // An empty unit list means two opposite things and the buyer needs to know
  // which. Rendering nothing (the previous behaviour) reads as "fine", so a
  // compound with nothing purchasable in it looked identical to one full of
  // options — the search matches on area before budget or bedrooms, so five
  // area hits can hold zero affordable units between them. Wording mirrors
  // voice/spoken_summary.py so the two surfaces cannot say different things.
  const unitsIndexed = compound.units_indexed || 0;
  const cheapest = compound.cheapest_unit_price;
  let availabilityNote = null;
  if (units.length === 0 && !unitsTotal) {
    availabilityNote = unitsIndexed && cheapest
      ? `No unit within budget — cheapest here is ${(Number(cheapest) / 1_000_000).toFixed(2)}M EGP`
      : 'No unit pricing on file for this compound yet';
  }

  const why = compound.why_recommended || '';
  const consider = compound.consider || '';
  const lifestyleMatch = compound.lifestyle_match || '';
  const whyPoints = asPoints(why);
  const considerPoints = asPoints(consider);
  const lifestylePoints = asPoints(lifestyleMatch);
  const hasInsights = !!(why || consider || lifestyleMatch);

  const saved = !!id && isSaved(id);

  const handleSave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      setRegisterPrompt({
        title: 'Save properties',
        message: 'Save properties by creating a free account.',
      });
      return;
    }
    if (id) toggleSave(id, compound);
  };

  return (
    <div className="compound-card">
      <div className="compound-card-image">
        {imgUrl ? (
          <img src={imgUrl} alt={name} />
        ) : (
          <div className="compound-card-image-placeholder" aria-hidden>
            <svg width="44" height="44" viewBox="0 0 48 48" fill="none" stroke="#9AA6BD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 40h40M8 40V16l16-6 16 6v24" />
              <rect x="17" y="24" width="14" height="8" rx="1" />
              <line x1="24" y1="24" x2="24" y2="32" />
              <line x1="17" y1="28" x2="31" y2="28" />
            </svg>
          </div>
        )}

        <button
          type="button"
          className={`compound-card-heart-btn${saved ? ' saved' : ''}`}
          onClick={handleSave}
          aria-label={saved ? 'Unsave compound' : 'Save compound'}
        >
          <Heart size={16} fill={saved ? '#E8A020' : 'none'} color={saved ? '#E8A020' : '#6B7A99'} />
        </button>

        {distanceKm != null && (
          <span className="compound-card-distance">
            <Navigation size={11} /> {Math.round(distanceKm)} km away
          </span>
        )}
      </div>

      <div className="compound-card-body">
        <h4 className="compound-card-title">{name}</h4>
        {location && (
          <p className="compound-card-meta">
            <MapPin size={12} className="compound-card-meta-icon" />
            <span>{location}</span>
          </p>
        )}

        {(area || hasPayment) && (
          <div className="compound-card-specs">
            {area && (
              <span className="compound-card-spec">
                <Maximize2 size={12} /> {area}
              </span>
            )}
            {hasPayment && <span className="compound-card-pill">Installments available</span>}
          </div>
        )}

        {!hasInsights && facilities && (
          <p className="compound-card-facilities">
            {facilities.length > 110 ? `${facilities.slice(0, 110).trim()}…` : facilities}
          </p>
        )}

        {availabilityNote && (
          <p className="compound-card-availability">{availabilityNote}</p>
        )}

        {units.length > 0 && (
        <div className="compound-card-units">
          <span className="compound-card-label">
            {unitsTotal > units.length
              ? `${unitsTotal} matching units · showing ${units.length}`
              : `${unitsTotal} matching unit${unitsTotal === 1 ? '' : 's'}`}
          </span>
          <ul className="compound-unit-list">
            {units.map((u) => (
              <li key={u.unit_id} className="compound-unit">
                <span className="compound-unit-spec">
                  {[u.no_of_rooms ? `${u.no_of_rooms} BR` : null,
                    u.unit_type,
                    u.area ? `${Math.round(u.area)} m²` : null]
                    .filter(Boolean).join(' · ')}
                </span>
                <span className="compound-unit-price">
                  {u.total_price
                    ? `${(u.total_price / 1_000_000).toFixed(u.total_price % 1_000_000 === 0 ? 0 : 1)}M`
                    : '—'}
                </span>
                {(u.installment_years || u.delivery_date) && (
                  <span className="compound-unit-terms">
                    {[u.installment_years ? `${u.installment_years}y plan` : null,
                      u.delivery_date ? `del. ${u.delivery_date}` : null]
                      .filter(Boolean).join(' · ')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
        )}

        {hasInsights && (
          <div className="compound-card-insights">
            {why && (
              <div className="compound-card-section">
                <span className="compound-card-label">Why it fits</span>
                {whyPoints.length > 0 ? (
                  <ul className="compound-card-points">{whyPoints.map((p, i) => <li key={i}>{p}</li>)}</ul>
                ) : (
                  <p className="compound-card-text">{why}</p>
                )}
              </div>
            )}
            {consider && (
              <div className="compound-card-section">
                <span className="compound-card-label">Consider</span>
                {considerPoints.length > 0 ? (
                  <ul className="compound-card-points">{considerPoints.map((p, i) => <li key={i}>{p}</li>)}</ul>
                ) : (
                  <p className="compound-card-text">{consider}</p>
                )}
              </div>
            )}
            {lifestyleMatch && (
              <div className="compound-card-section">
                <span className="compound-card-label">Lifestyle match</span>
                {lifestylePoints.length > 0 ? (
                  <ul className="compound-card-points">{lifestylePoints.map((p, i) => <li key={i}>{p}</li>)}</ul>
                ) : (
                  <p className="compound-card-text">{lifestyleMatch}</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Link to={`/compound/${id}`} className="compound-card-footer">
        Chat about this <ChevronRight size={14} />
      </Link>

      <RegisterPromptModal
        open={!!registerPrompt}
        onClose={() => setRegisterPrompt(null)}
        title={registerPrompt?.title}
        message={registerPrompt?.message}
      />
    </div>
  );
}
